import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, MapPin, Star, ListChecks } from "lucide-react";

export type DealerPublicProfile = {
  username: string;
  dealershipName: string | null;
  city: string | null;
  stateCode: string | null;
  address: string | null;
  avgRating: number | null;
  reviewCount: number;
  activeListings: number;
};

export function DealerProfileDialog({
  userId,
  displayName,
  open,
  onClose,
}: {
  userId: string;
  displayName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { data: profile, isLoading } = useQuery<DealerPublicProfile>({
    queryKey: ["/api/marketplace/dealer", userId, "public-profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/dealer/${userId}/public-profile`);
      return res.json();
    },
    enabled: open,
  });

  const name = profile?.dealershipName || profile?.username || displayName;
  const location = [profile?.city, profile?.stateCode].filter(Boolean).join(", ");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-dealer-profile">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-violet-500 shrink-0" />
            {isLoading ? <Skeleton className="h-5 w-40" /> : name}
          </DialogTitle>
          {!isLoading && profile?.dealershipName && (
            <DialogDescription className="text-xs text-muted-foreground">
              @{profile.username}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : profile ? (
          <div className="space-y-3 py-1">
            {(location || profile.address) && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  {profile.address && <p className="text-foreground">{profile.address}</p>}
                  {location && <p className="text-muted-foreground">{location}</p>}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-amber-400 shrink-0" />
              {profile.avgRating != null ? (
                <span>
                  <span className="font-semibold">{profile.avgRating.toFixed(1)}</span>
                  <span className="text-muted-foreground ml-1">({t("marketplace:dealerProfile.reviews", { count: profile.reviewCount })})</span>
                </span>
              ) : (
                <span className="text-muted-foreground">{t("marketplace:dealerProfile.noReviews")}</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ListChecks className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">
                {t("marketplace:dealerProfile.activeListings", { count: profile.activeListings })}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-2">{t("marketplace:dealerProfile.notAvailable")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
